import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { DriverDailyMetricDto } from '@/types';

interface DailyCallsChartProps {
  dailyMetrics?: DriverDailyMetricDto[];
  isLoading?: boolean;
}

// Beautiful gradient colors for the bars
const BAR_COLORS = {
  default: '#2a5345', // Green base
  highlight: '#1e3d38', // Darker green
};

export function DailyCallsChart({
  dailyMetrics = [],
  isLoading,
}: DailyCallsChartProps) {
  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!dailyMetrics || dailyMetrics.length === 0) {
      // Return empty data for last 7 days if no data
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return days.map((day) => ({
        day,
        completedCalls: 0,
        date: '',
      }));
    }

    return dailyMetrics.map((metric) => ({
      day: metric.day,
      completedCalls: metric.completedCalls,
      date: metric.date,
    }));
  }, [dailyMetrics]);

  // Find max value for highlighting
  const maxCalls = useMemo(() => {
    return Math.max(...chartData.map((d) => d.completedCalls), 0);
  }, [chartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm text-gray-900">{data.date || label}</p>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {payload[0].value}
            </span>{' '}
            calls completed
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5" style={{ color: '#2a5345' }} />
            Daily Calls (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[250px] flex items-center justify-center">
          <div className="animate-pulse text-gray-600">
            Loading chart data...
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalCalls = chartData.reduce((sum, d) => sum + d.completedCalls, 0);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5" style={{ color: '#2a5345' }} />
            Daily Calls (Last 7 Days)
          </CardTitle>
          <span className="text-sm text-gray-600">
            Total:{' '}
            <span className="font-semibold text-gray-900">{totalCalls}</span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="h-[250px] pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3d7066" stopOpacity={1} />
                <stop offset="100%" stopColor="#2a5345" stopOpacity={1} />
              </linearGradient>
              <linearGradient
                id="barGradientHighlight"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#4a8a74" stopOpacity={1} />
                <stop offset="100%" stopColor="#3d7066" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
              opacity={0.5}
            />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              allowDecimals={false}
              width={30}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: '#f3f4f6', opacity: 0.3 }}
            />
            <Bar dataKey="completedCalls" radius={[6, 6, 0, 0]} maxBarSize={50}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.completedCalls === maxCalls && maxCalls > 0
                      ? 'url(#barGradientHighlight)'
                      : 'url(#barGradient)'
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

